/**
 * Where "Back to Orders" goes. The Orders list hands each screen it opens the list's own address
 * (filter included) in router state, and the order screen passes it on to a consignment invoice,
 * so coming back lands on the same filtered list. Opened from a bookmark or a pasted link there is
 * no state, and the fallback is the Orders list itself — never a dead end.
 *
 * Every "invoice" in this file is the DESPATCH document, the CONSIGNMENT INVOICE (R-23/R-26). The
 * purchase invoice the retailer pays lives on R-22 Payables and is never a Back target from here.
 *
 * Only a path inside the Orders section is honoured: router state is not trusted to name any address.
 */
export const ORDERS_HOME = '/shop/orders';

const inOrders = (to: unknown): to is string =>
  typeof to === 'string' && !to.startsWith('//')
  && (to === ORDERS_HOME || to.startsWith(`${ORDERS_HOME}?`) || to.startsWith(`${ORDERS_HOME}/`));

/** The retailer's Consignment Invoices list (R-23) — also somewhere one can be opened from. */
export const INVOICES_HOME = '/shop/consignment-invoices';

const inInvoices = (to: unknown): to is string =>
  typeof to === 'string' && !to.startsWith('//') && (to === INVOICES_HOME || to.startsWith(`${INVOICES_HOME}?`));

/**
 * Where Back on a consignment invoice goes: the R-23 list it was opened from, else the order
 * (see `backToOrders`).
 */
export function backFromInvoice(state: unknown): string {
  const to = (state as { backTo?: unknown } | null | undefined)?.backTo;
  return inInvoices(to) ? to : backToOrders(state);
}

export function backToOrders(state: unknown): string {
  const to = (state as { backTo?: unknown } | null | undefined)?.backTo;
  return inOrders(to) ? to : ORDERS_HOME;
}

/**
 * An order opened from the list passes a consignment invoice TWO addresses: `backTo`, the order
 * itself (Back on the invoice returns to that exact order), and `then`, the list the order was
 * opened from, which the order gets back as its own `backTo` so its "Back to Orders" still lands
 * on the same filtered list.
 */
export function invoiceLinkState(orderPath: string, ordersBackTo: string) {
  return { backTo: orderPath, then: ordersBackTo };
}

/**
 * What the consignment invoice's Back link hands the page it returns to (the order's own way back
 * to the list).
 */
export function stateAfterBack(state: unknown): { backTo: string } | undefined {
  const then = (state as { then?: unknown } | null | undefined)?.then;
  return inOrders(then) ? { backTo: then } : undefined;
}
